<template>
  <div>
    <molecules-hero-header
      outer-min-height="min-h-auto"
      class="py-6 lg:py-[5.25rem]"
      :image-config="{
        img: `${APP_ASSET_BASEURL}edge/static/images/products/car-insurance/comprehensive.svg`,
        class: 'max-w-full w-[30.5rem]',
        dimensions: {
          width: 488,
          height: 318
        }
      }"
      :buttons="[
        {
          theme: 'primary',
          text: 'See partner offers',
          url: verticalData.formPage
        }
      ]"
      title-tag="h1"
    >
      <template #header>
        <strong class="font-extrabold">Compare Comprehensive Car Insurance</strong>
      </template>

      <template #sub-header>
        Compare and find comprehensive car insurance with our panel of insurers to find a premium and cover that can meet your budget.
      </template>
    </molecules-hero-header>

    <MoleculesHomeInfoBlocks
      class="bg-gray-10"
      :items="INFO_BLOCKS"
    />

    <molecules-home-comparison-blocks
      class="bg-gray-350"
      :items="INFO_TABLE"
    >
      <template #header>
        What’s usually <strong class="font-extrabold">covered</strong> by<br />
        comprehensive car insurance?
      </template>

      <template #sub-header>
        While coverage can vary from insurer to insurer and policy to policy, comprehensive car<br />
        insurance generally includes the following:
      </template>
    </molecules-home-comparison-blocks>

    <molecules-home-saving-plan
      :featured-image-url="`${APP_ASSET_BASEURL}edge/static/images/products/car-insurance/saving.svg`"
      :featured-image-dimensions="{
        width: 291,
        height: 343
      }"
      :verticals="SAVING_PLAN_ITEMS"
      card-container-class="md:!max-w-[36.375rem]"
      theme="card"
      class="bg-gray-10"
      image-lazy-load
    >
      <template #header>
        <strong class="font-extrabold">How much</strong> does comprehensive<br />
        car insurance cost?
      </template>

      <template #sub-header>
        This is how the insurer calculates how much your car insurance will cost.<br />
        The cost depends on a range of factors, including:
      </template>

      <template #footer>
        <div class="flex flex-col justify-center">
          <p class="text-left lg:text-center text-lg">
            When it comes to insurance, there's no such thing as "too much." No matter what your situation looks like, it’s always worth shopping around to see where you can find the best deal. That’s where our panel of insurers comes in. It could help you save significantly every year.
          </p>

          <atoms-button
            class="mt-8 mx-auto w-full md:w-auto"
            size="large"
            theme="primary"
            :to="VERTICALS[VERTICAL.CAR_INSURANCE].formPage"
          >
            Get a quick quote
          </atoms-button>
        </div>
      </template>
    </molecules-home-saving-plan>

    <molecules-home-info-blocks
      size="compact"
      class="bg-gray-350"
      inner-class="max-w-wrapper-md mx-auto"
      :items="AT_A_GLANCE_ITEMS"
      image-lazy-load
    >
      <template #header>
        <strong class="font-extrabold">
          Comprehensive car
          <br class="block md:hidden" />
          insurance
        </strong>
        at a glance
      </template>
    </molecules-home-info-blocks>

    <molecules-home-great-deal
      :cards="GREAT_DEAL_ITEMS"
      :col-count="3"
      card-wrapper-max-width="w-wrapper-lg"
      card-class="p-6"
      image-lazy-load
    >
      <template #header>
        Want a <strong class="font-extrabold">great deal</strong> on comprehensive car
        <br class="lg:block hidden" />
        insurance?
        <strong class="font-extrabold">Compare Club can help.</strong>
      </template>

      <template #sub-header>
        There’s no need to spend hours searching for cheaper comprehensive car insurance.
        <br class="lg:block hidden" />
        Our panel of insurers means you can find great value policies in a matter of minutes.
      </template>
    </molecules-home-great-deal>

    <molecules-home-footer-info theme="white-boxed">
      <template #title>
        Things you should know
      </template>

      <template #description>
        <p>
          Compare Club Car Insurance is an online financial comparison service and is owned and operated by Compare Club Australia Pty Ltd (ACN: 634 600 007). Compare Club does not compare all brands or all  products offered by all brands.
        </p>

        <p>
          The financial products compared on this website do not necessarily compare all features that may be relevant
          to you. Comparisons are made on the basis of price only and different products may have different features.
        </p>

        <p>
          The information on this page is opinion only. Please check with a financial professional before you make any
          major financial decisions.
        </p>

        <p>
          GENERAL ADVICE WARNING: General Advice is advice that has been prepared without considering your current
          objectives, financial situation or needs. Therefore, before acting on this advice, you should consider the
          appropriateness of the advice having regard to those objectives, situation or needs.
        </p>

        <p>
          If the advice provided relates to the acquisition or possible acquisition of a new insurance policy you should consider the insurers PDS prior to making the decision to purchase their product. Information regarding the income we have been paid by the insurer for this transaction is available upon request. Compare club will received a fee from the insurer if you buy their product, this fee is normally a commission of up to 10% of the base premium. This pays for our service in obtaining data and providing our website to assist the insurer marketing their product.
        </p>
      </template>
    </molecules-home-footer-info>
  </div>
</template>

<script setup>
import {
  SAVING_PLAN_ITEMS,
  INFO_BLOCKS,
  GREAT_DEAL_ITEMS,
  INFO_TABLE,
  AT_A_GLANCE_ITEMS
} from '~/constants/pages/comprehensive'

defineOptions({
  name: 'CarInsuranceComprehensive'
})

const trustpilotStore = useTrustpilotStore()

const _VERTICAL = VERTICAL.CAR_INSURANCE
const verticalData = VERTICALS[_VERTICAL]
const $route = useRoute()

const PAGE_DETAILS = {
  title: 'Compare Comprehensive Car Insurance | Compare Club',
  description: 'Find comprehensive car insurance quotes at a cost you will like. Click here to compare with Compare Club.',
  ogUrl: trailingSlash(`${CC_DOMAIN}${$route.path}`),
  ogType: 'product'
}

generateMeta({
  title: PAGE_DETAILS.title,
  ogTitle: PAGE_DETAILS.title,
  description: PAGE_DETAILS.description,
  ogDescription: PAGE_DETAILS.description,
  ogUrl: PAGE_DETAILS.ogUrl,
  ogType: PAGE_DETAILS.ogType,
  canonical: PAGE_DETAILS.ogUrl
}, pageIsFunnel($route))

useJsonld(() => {
  const _path = trailingSlash($route.path)

  const {
    trustScore,
    numberOfReviews
  } = trustpilotStore.trustpilotData ?? {}

  return {
    '@context': 'https://schema.org',
    '@graph': [
      GENERATE_WEBPAGE_SCHEMA({
        page: _path,
        pageTitle: PAGE_DETAILS.title,
        pageDescription: PAGE_DETAILS.description,
        aboutPath: `${_path}#service`,
        mainEntityPath: `${_path}#service`,
        primaryImageOfPagePath: '/#logo',
        breadcrumbPath: `${_path}#breadcrumb`
      }),
      GENERATE_SERVICE_SCHEMA({
        page: _path,
        name: 'Compare Comprehensive Car Insurance',
        serviceType: 'Insurance comparison',
        description: PAGE_DETAILS.description,
        audienceName: 'Australian consumers comparing comprehensive car insurance',
        servicePhone: '+611300757819',
        trustpilot: {
          rating: trustScore,
          count: numberOfReviews?.total
        }
      }),
      GENERATE_BREADCRUMB_SCHEMA(_path)
    ]
  }
})
</script>
